// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import {
    convertDateOnlyStringToDate,
    convertDateToDateOnlyString,
} from "@insite/client-framework/Common/Utilities/DateUtilities";
import { getShipTosDataView } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { OptionObject } from "@insite/mobius/DynamicDropdown";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Tag, { TagPresentationProps } from "@insite/mobius/Tag";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import SearchFieldCustomerPartNumber from "@steiner/App/Components/ReorderPad/SearchFieldCustomerPartNumber";
import SearchFieldDateRange from "@steiner/App/Components/ReorderPad/SearchFieldDateRange";
import SearchFieldMfgrNumber from "@steiner/App/Components/ReorderPad/SearchFieldMfgrNumber";
import SearchFieldProductDescription from "@steiner/App/Components/ReorderPad/SearchFieldProductDescription";
import SearchFieldSelectShipTo, { shipTosParameter } from "@steiner/App/Components/ReorderPad/SearchFieldSelectShipTo";
import SearchFieldSelectSortType from "@steiner/App/Components/ReorderPad/SearchFieldSelectSortType";
import clearReorderPadSearchParams from "@steiner/App/Handlers/ReorderPad/ClearReorderPadSearchParams";
import loadReorderPad, { defaultReorderPadParameters } from "@steiner/App/Handlers/ReorderPad/LoadReorderPad";
import updateReorderPadSearchFields from "@steiner/App/Handlers/ReorderPad/UpdateReorderPadSearchFields";
import { ReorderPadApiParameter } from "@steiner/App/Services/ReorderPad";
import { CustomAppState } from "@steiner/App/Store/Reducers";
import { DatePickerState } from "@steiner/MobiusOverrides/DatePicker";
import { GridContainerProps } from "@steiner/MobiusOverrides/GridContainer/GridContainer";
import { XNGAGE_ELEMENTS } from "@steiner/XDL/constants";
import { xScreen } from "@steiner/XDL/Utilities";
import debounce from "lodash/debounce";
import React, { useState } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import { css } from "styled-components";

const enum fields {
    showTagsOnly = "showTagsOnly",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showTagsOnly]: boolean;
    };
}

const mapStateToProps = (state: CustomAppState) => ({
    pagination: state.xReorderPad.pagination,
    filters: state.xReorderPad.searchParams,
    shipTosDataView: getShipTosDataView(state, shipTosParameter),
});

const mapDispatchToProps = {
    loadReorderPad,
    updateReorderPadSearchFields,
    clearReorderPadSearchParams,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ReorderPadFiltersStyles {
    filtersGridContainer?: GridContainerProps;
    appliedFiltersContainer?: InjectableCss;
    appliedFilterTag?: TagPresentationProps;
    filterButtonsGridItem?: GridItemProps;
    clearFiltersButton?: ButtonPresentationProps;
}

export const reorderPadFiltersStyles: ReorderPadFiltersStyles = {};

const styles = reorderPadFiltersStyles;

const ReorderPadFilters = ({
    filters,
    pagination,
    updateReorderPadSearchFields,
    clearReorderPadSearchParams,
    fields,
    shipTosDataView,
}: Props) => {
    const [searchParams, setSearchParams] = useState<ReorderPadApiParameter>(defaultReorderPadParameters);
    const [options, setOptions] = useState<{ label: string; value: string }[]>([]);
    React.useEffect(() => {
        if (filters) {
            setSearchParams(filters); // Sync local state with Redux state
        }
    }, [filters]);
    React.useEffect(() => {
        if (pagination?.pageSizeOptions) {
            const options =
                pagination &&
                pagination.sortOptions.map(option => ({ label: option.displayName, value: option.sortType }));
            setOptions(options);
        }
    }, [pagination?.pageSizeOptions]);

    const debouncedUpdateReorderPadFilters = React.useMemo(
        () => debounce(updateReorderPadSearchFields, 300),
        [updateReorderPadSearchFields],
    );

    const onChangeFilter = (filterKey: string, value: any) => {
        if (searchParams[filterKey as keyof typeof searchParams] === value) {
            return;
        }

        setSearchParams(prev => {
            const updatedParams = { ...prev, [filterKey]: value };
            debouncedUpdateReorderPadFilters({
                key: filterKey,
                value,
            });
            return updatedParams; // Return the updated state
        });
    };

    const onDeleteFilter = (filterKey: string, filterValue?: string) => {
        setSearchParams(prev => ({
            ...prev,
            [filterKey]: filterValue ?? undefined, // Reset only the specific key
        }));
        debouncedUpdateReorderPadFilters({
            key: filterKey,
            value: filterValue,
        });
    };
    const clearSearchHandler = () => {
        setSearchParams(defaultReorderPadParameters);
        clearReorderPadSearchParams({});
    };

    const getCustomerSequence = (customerSequence?: string) => {
        if (customerSequence === "-1") {
            return "-1";
        }

        const customerSequenceValue = shipTosDataView?.value?.find(o => o.customerSequence === customerSequence)?.customerSequence;
        if (customerSequence && !customerSequenceValue) {
            return translate("Billing Address");
        }

        return customerSequence;
    };

    if (fields.showTagsOnly) {
        const customer = getCustomerSequence(filters?.customerSequence);
        return (
            <StyledWrapper {...styles.appliedFiltersContainer}>
                {customer !== "-1" && (
                    <Tag {...styles.appliedFilterTag} onDelete={() => onDeleteFilter("customerSequence", "-1")}>
                        {`${translate("Ship To")}: ${customer}`}
                    </Tag>
                )}
                {filters?.productDescription && (
                    <Tag {...styles.appliedFilterTag} onDelete={() => onDeleteFilter("productDescription")}>
                        {`${translate("Product Description #")}: ${filters.productDescription}`}
                    </Tag>
                )}
                {filters?.fromDate && (
                    <Tag {...styles.appliedFilterTag} onDelete={() => onDeleteFilter("fromDate")}>
                        {`${translate("From")}: ${filters.fromDate}`}
                    </Tag>
                )}
                {filters?.toDate && (
                    <Tag {...styles.appliedFilterTag} onDelete={() => onDeleteFilter("toDate")}>
                        {`${translate("To")}: ${filters.toDate}`}
                    </Tag>
                )}
                {filters?.customerPartNumber && (
                    <Tag {...styles.appliedFilterTag} onDelete={() => onDeleteFilter("customerPartNumber")}>
                        {`${translate("Customer Part #")}: ${filters.customerPartNumber}`}
                    </Tag>
                )}
                {filters?.mfgrNumber && (
                    <Tag {...styles.appliedFilterTag} onDelete={() => onDeleteFilter("mfgrNumber")}>
                        {`${translate("MFG #")}: ${filters.mfgrNumber}`}
                    </Tag>
                )}
                {filters?.sortBy && (
                    <Tag {...styles.appliedFilterTag} onDelete={() => onDeleteFilter("sortBy")}>
                        {`${translate("Sort by")}: ${filters.sortBy}`}
                    </Tag>
                )}
            </StyledWrapper>
        );
    }
    return (
        <GridContainer {...styles.filtersGridContainer} gap={12}>
            <SearchFieldSelectShipTo
                selectProps={{
                    value: searchParams.customerSequence ?? defaultReorderPadParameters.customerSequence,
                    label: translate("Ship-To Address"),
                    onChange: id => {
                        onChangeFilter("customerSequence", id);
                    },
                }}
            />
            <SearchFieldProductDescription
                textFieldProps={{
                    value: searchParams.productDescription ?? defaultReorderPadParameters.productDescription,
                    label: translate("Product Description"),
                    placeholder: translate("Enter Product Description"),
                    onChange: e => {
                        onChangeFilter("productDescription", e.target.value);
                    },
                }}
            />
            <SearchFieldCustomerPartNumber
                textFieldProps={{
                    value: searchParams.customerPartNumber ?? defaultReorderPadParameters.customerPartNumber,
                    label: translate("Customer Part #"),
                    placeholder: translate("Enter Customer Part #"),
                    onChange: e => {
                        onChangeFilter("customerPartNumber", e.target.value);
                    },
                }}
            />
            <SearchFieldMfgrNumber
                textFieldProps={{
                    value: searchParams.mfgrNumber ?? defaultReorderPadParameters.mfgrNumber,
                    label: translate("MFGR #"),
                    placeholder: translate("Enter MFGR #"),
                    onChange: e => {
                        onChangeFilter("mfgrNumber", e.target.value);
                    },
                }}
            />
            <SearchFieldSelectSortType
                options={options}
                selectProps={{
                    value: searchParams.sortBy ?? defaultReorderPadParameters.sortBy,
                    label: translate("Sort By"),
                    onChange: e => {
                        onChangeFilter("sortBy", e.target.value);
                    },
                }}
            />
            <SearchFieldDateRange
                fromDate={convertDateOnlyStringToDate(searchParams.fromDate)}
                toDate={convertDateOnlyStringToDate(searchParams.toDate)}
                fromDateChangeHandler={({ selectedDay }: Pick<DatePickerState, "selectedDay">) => {
                    onChangeFilter("fromDate", convertDateToDateOnlyString(selectedDay));
                }}
                toDateChangeHandler={({ selectedDay }: Pick<DatePickerState, "selectedDay">) => {
                    onChangeFilter("toDate", convertDateToDateOnlyString(selectedDay));
                }}
            />
            <GridItem {...styles.filterButtonsGridItem}>
                <Button {...styles.clearFiltersButton} onClick={clearSearchHandler}>
                    {translate("Clear")}
                </Button>
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ReorderPadFilters),
    definition: {
        group: XNGAGE_ELEMENTS,
        displayName: "Reorder Pad Filters",
        allowedContexts: ["ReorderPadPage"],
        fieldDefinitions: [
            {
                name: fields.showTagsOnly,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 1,
            },
        ],
    },
};

export default widgetModule;
